export type MetricByTimeFormat = "integer" | "percent" | "hours" | "ratio" | "conversion";

export type MetricByTimeOption = {
    key: string;
    label: string;
    format: MetricByTimeFormat;
};

export type GenreExplorerTimeView = "weekly" | "dow";

export type GenreExplorerMetricsByTimePoint = {
    key: string;
    value: number | null;
};

const DEFAULT_METRIC_KEY = "video_start";

export const METRIC_BY_TIME_OPTIONS: MetricByTimeOption[] = [
    { key: "video_start", label: "Video Starts", format: "integer" },
    { key: "views", label: "Views", format: "integer" },
    { key: "users", label: "Users", format: "integer" },
    { key: "plays", label: "Plays", format: "integer" },
    { key: "avg_percent_watched", label: "Avg % Watched", format: "percent" },
    { key: "hours_watched", label: "Hours Watched", format: "hours" },
    { key: "video_start_per_user", label: "Video Starts / User", format: "ratio" },
    { key: "plays_per_user", label: "Plays / User", format: "ratio" },
    { key: "session_conversion", label: "Session Conversion", format: "conversion" }
];

const normalizeNumber = (value: unknown): number | null => {
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === "string") {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }

    return null;
};

export const ALL_GENRES_VALUE = "All Genres";

export const getDefaultMetricByTime = (): string => DEFAULT_METRIC_KEY;

export const getMetricByTimeOption = (metricKey: string): MetricByTimeOption => {
    return METRIC_BY_TIME_OPTIONS.find(option => option.key === metricKey) || METRIC_BY_TIME_OPTIONS[0];
};

export const toChartMetricValue = (value: number | null, format: MetricByTimeFormat): number | null => {
    if (value === null || value === undefined) {
        return null;
    }

    if (format === "conversion") {
        return value * 100;
    }

    return value;
};

export const formatMetricByTimeValue = (value: number | null, format: MetricByTimeFormat): string => {
    if (value === null || value === undefined) {
        return "--";
    }

    if (format === "integer") {
        return Math.round(value).toLocaleString();
    }

    if (format === "percent") {
        return `${value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
    }

    if (format === "conversion") {
        const converted = value * 100;
        return `${converted.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
    }

    if (format === "hours" || format === "ratio") {
        return value.toFixed(2);
    }

    return value.toLocaleString();
};

export const mapWeeklyMetricsByTimeRows = (rows: unknown[]): GenreExplorerMetricsByTimePoint[] => {
    if (!Array.isArray(rows)) {
        return [];
    }

    return rows
        .map((row: unknown) => {
            const item = row as Record<string, unknown>;
            const weekValue = item.week;
            if (typeof weekValue !== "string" || weekValue.trim() === "") {
                return null;
            }

            return {
                key: weekValue,
                value: normalizeNumber(item.value)
            };
        })
        .filter((item): item is GenreExplorerMetricsByTimePoint => item !== null);
};

export const mapDowMetricsByTimeRows = (rows: unknown[]): GenreExplorerMetricsByTimePoint[] => {
    if (!Array.isArray(rows)) {
        return [];
    }

    return rows
        .map((row: unknown) => {
            const item = row as Record<string, unknown>;
            const dayValue = item.day;
            if (typeof dayValue !== "string" || dayValue.trim() === "") {
                return null;
            }

            return {
                key: dayValue,
                value: normalizeNumber(item.value)
            };
        })
        .filter((item): item is GenreExplorerMetricsByTimePoint => item !== null);
};
